<template>
  <div class="space-y-8 max-w-4xl mx-auto px-4 sm:px-6">
    <!-- Hero Section -->
    <div class="space-y-6">
      <!-- SEO-optimized eyebrow -->
      <p
        class="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold"
      >
        SHAB-E-QADR 2026 - PAKISTAN
      </p>

      <!-- Hero section -->
      <div class="space-y-4">
        <!-- H1 - SEO optimized -->
        <h1
          class="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-gray-100 leading-tight"
        >
          {{ lang.header.romanUrduTitle }}
        </h1>

        <!-- Subtitle with context -->
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          {{ lang.dates.description }}
        </p>
      </div>

      <!-- Most Likely Night Card - Gold emphasis -->
      <div
        class="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/50 dark:to-yellow-950/50 rounded-2xl p-6 border-2 border-amber-300 dark:border-amber-700 shadow-lg"
      >
        <div class="flex items-start gap-3 mb-3">
          <svg
            class="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
            />
          </svg>
          <div class="flex-1">
            <p
              class="text-sm font-bold text-amber-700 dark:text-amber-400 mb-1 uppercase tracking-wide"
            >
              Most Likely
            </p>
            <p
              class="text-4xl sm:text-5xl font-black text-gray-900 dark:text-gray-100 mb-2"
            >
              27 Ramadan
            </p>
            <p
              class="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-3"
            >
              16 March 2026 ki raat
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ lang.dates.mostLikely }}
            </p>
          </div>
        </div>
        <div
          class="flex items-center gap-2 text-sm sm:text-base text-amber-800 dark:text-amber-300 bg-white/60 dark:bg-gray-800/60 rounded-lg px-3 py-2 w-fit"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="font-semibold">{{ lang.header.worshipValue }}</span>
        </div>
      </div>

      <!-- All Possible Nights -->
      <div class="space-y-3">
        <h2
          class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100"
        >
          Mumkin Taaq Raatein
        </h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Shab-e-Qadr in mein se kisi bhi raat ho sakti hai. Tamam taaq raaton
          mein ibadat karein.
        </p>

        <div class="grid sm:grid-cols-2 gap-3">
          <!-- 21 Ramadan -->
          <div
            class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <p class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              21 Ramadan
            </p>
            <p
              class="text-base font-semibold text-gray-600 dark:text-gray-400 mb-2"
            >
              10 March 2026 ki raat
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              Pehli taaq raat
            </p>
          </div>

          <!-- 23 Ramadan -->
          <div
            class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <p class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              23 Ramadan
            </p>
            <p
              class="text-base font-semibold text-gray-600 dark:text-gray-400 mb-2"
            >
              12 March 2026 ki raat
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              Doosri taaq raat
            </p>
          </div>

          <!-- 25 Ramadan -->
          <div
            class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <p class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              25 Ramadan
            </p>
            <p
              class="text-base font-semibold text-gray-600 dark:text-gray-400 mb-2"
            >
              14 March 2026 ki raat
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              Teesri taaq raat
            </p>
          </div>

          <!-- 27 Ramadan - Highlighted -->
          <div
            class="bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-xl p-4 border-2 border-amber-300 dark:border-amber-700 relative overflow-hidden"
          >
            <div class="absolute top-2 right-2">
              <svg
                class="w-5 h-5 text-amber-500 dark:text-amber-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </div>
            <p class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              27 Ramadan ⭐
            </p>
            <p
              class="text-base font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              16 March 2026 ki raat
            </p>
            <p class="text-xs font-semibold text-amber-700 dark:text-amber-400">
              Pakistan mein aksar yahi raat mani jati hai
            </p>
          </div>

          <!-- 29 Ramadan -->
          <div
            class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700 sm:col-span-2"
          >
            <p class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              29 Ramadan
            </p>
            <p
              class="text-base font-semibold text-gray-600 dark:text-gray-400 mb-2"
            >
              18 March 2026 ki raat
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              Aakhri taaq raat (agar 30 Ramadan hon)
            </p>
          </div>
        </div>
      </div>

      <!-- Important Note -->
      <div
        class="bg-blue-50 dark:bg-blue-950/40 rounded-xl p-5 border border-blue-200 dark:border-blue-800"
      >
        <div class="flex items-start gap-3">
          <svg
            class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clip-rule="evenodd"
            />
          </svg>
          <div class="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            <p class="font-semibold text-gray-900 dark:text-gray-100">
              Yaad Rakhein:
            </p>
            <p>
              Shab-e-Qadr ka asli waqt sirf Allah janta hai. Isliye tamam taaq
              raaton mein ibadat ka ehtimam karein. Har taaq raat ko Shab-e-Qadr
              samajh kar guzarein.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Fazilat Section -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ lang.significance.title }}
      </h2>
      <div class="prose dark:prose-invert max-w-none">
        <p class="text-base leading-relaxed text-gray-700 dark:text-gray-300">
          {{ lang.significance.quranRef }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <div
          v-for="(virtue, index) in lang.significance.virtues"
          :key="index"
          class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl p-5 border border-emerald-200 dark:border-emerald-800"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ virtue }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Ibadat Ka Tarika -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ lang.worship.title }}
      </h2>
      <p class="text-base text-gray-700 dark:text-gray-300">
        {{ lang.worship.intro }}
      </p>

      <div class="space-y-3">
        <div
          v-for="(activity, index) in lang.worship.activities"
          :key="index"
          class="flex items-start gap-3 bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div
            class="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center"
          >
            <span
              class="text-xs font-bold text-emerald-700 dark:text-emerald-300"
              >{{ index + 1 }}</span
            >
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-300">{{ activity }}</p>
        </div>
      </div>

      <!-- Special Dua Card -->
      <div
        class="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40 rounded-2xl p-6 border-2 border-purple-200 dark:border-purple-800 mt-6"
      >
        <div class="flex items-center gap-2 mb-4">
          <svg
            class="w-6 h-6 text-purple-600 dark:text-purple-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
          <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            Shab-e-Qadr Ki Khas Dua
          </h3>
        </div>

        <div class="space-y-4">
          <div class="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4">
            <p
              class="text-2xl sm:text-3xl font-arabic text-center text-gray-900 dark:text-gray-100 leading-loose mb-3"
              dir="rtl"
            >
              {{ lang.worship.specialDua.arabic }}
            </p>
            <p
              class="text-base sm:text-lg font-semibold text-center text-purple-700 dark:text-purple-300 mb-2"
            >
              {{ lang.worship.specialDua.roman }}
            </p>
            <p
              class="text-sm text-center text-gray-600 dark:text-gray-400 italic"
            >
              {{ lang.worship.specialDua.urdu }}
            </p>
          </div>
          <p class="text-xs text-gray-600 dark:text-gray-400 text-center">
            Hazrat Aisha (RA) se riwayat hai ke Rasulullah (SAW) ne farmaya:
            "Shab-e-Qadr mein yeh dua zyada parhein"
          </p>
        </div>
      </div>
    </div>

    <!-- Signs of Laylatul Qadr -->
    <div class="space-y-4">
      <h2
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
      >
        {{ lang.practical.title }}
      </h2>

      <div
        class="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-5 border border-amber-200 dark:border-amber-800"
      >
        <h3
          class="font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2"
        >
          <svg
            class="w-5 h-5 text-amber-600 dark:text-amber-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          Shab-e-Qadr Ki Nishaniyan
        </h3>
        <ul class="space-y-2">
          <li
            v-for="(sign, index) in lang.practical.signs"
            :key="index"
            class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <span class="text-amber-600 dark:text-amber-400">•</span>
            <span>{{ sign }}</span>
          </li>
        </ul>
      </div>

      <div class="grid sm:grid-cols-2 gap-4">
        <div
          v-for="(tip, index) in lang.practical.tips"
          :key="index"
          class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ tip }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- SEO Content - Collapsible -->
    <details class="group mt-8">
      <summary class="cursor-pointer list-none">
        <div
          class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <span class="font-semibold text-gray-900 dark:text-gray-100">
            Shab-e-Qadr 2026 - Complete Guide & FAQs
          </span>
          <svg
            class="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </summary>

      <div class="mt-4 space-y-6 text-gray-700 dark:text-gray-300">
        <!-- SEO-optimized intro paragraph -->
        <div class="prose dark:prose-invert max-w-none">
          <p class="text-base leading-relaxed">
            Shab-e-Qadr 2026 Pakistan mein Ramadan ki aakhri 10 taaq raaton mein
            aati hai. Sabse zyada ehtemal <strong>27 Ramadan 2026</strong> ko
            hai jo <strong>16 March 2026</strong> ki raat hai. Shab-e-Qadr, jise
            Lailatul Qadr bhi kaha jata hai, woh mubarak raat hai jis mein Quran
            Pak nazil hua. Is raat ki ibadat
            <strong>hazar mahino se behtar</strong> hai. Pakistan mein aam taur
            par 27 Ramadan ki raat ko Shab-e-Qadr mana jata hai, lekin tamam
            taaq raaton (21, 23, 25, 27, 29 Ramadan) mein ibadat ka ehtimam
            karna chahiye.
            <a
              href="/itikaf/"
              class="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
              >Itikaf</a
            >
            ke dauran in raaton ki talash karna zyada ajar ka bais hai.
          </p>
        </div>

        <!-- FAQ Section for rich results -->
        <div class="space-y-4">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            Frequently Asked Questions
          </h2>

          <div class="space-y-3">
            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Shab-e-Qadr 2026 kab hai Pakistan mein?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Shab-e-Qadr 2026 Pakistan mein Ramadan ki aakhri 10 taaq raaton
                mein aati hai. Sabse zyada ehtemal
                <strong>27 Ramadan ko hai jo 16 March 2026</strong> ki raat hai.
                Lekin yeh 21, 23, 25, ya 29 Ramadan ki raat bhi ho sakti hai.
                Tamam taaq raaton mein ibadat ka ehtimam karein.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Shab-e-Qadr ki kya fazilat hai?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Shab-e-Qadr hazar mahino se behtar hai. Is raat ki ibadat 83
                saal 4 mahine ki ibadat se zyada ajar rakhti hai. Allah ne Surah
                Al-Qadr mein farmaya: "Shab-e-Qadr hazar mahino se behtar hai."
                Is raat farishte zameen par utarte hain aur subah tak salamti
                hoti hai.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Shab-e-Qadr mein kaunsi dua parhein?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Shab-e-Qadr ki sabse aham dua hai:
                <strong
                  >"Allahumma innaka afuwwun tuhibbul afwa fa'fu anni"</strong
                >
                (Ya Allah tu maaf karne wala hai, tu mujhe maaf kar de). Hazrat
                Aisha (RA) se riwayat hai ke Rasulullah (SAW) ne yeh dua
                Shab-e-Qadr mein parhne ka farmaya. Is ke ilawa Quran tilawat,
                nafl namaz, tauba astaghfar aur darood sharif parhein.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Shab-e-Qadr ki pehchan kya hai?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Shab-e-Qadr ki kuch nishaniyan hain: Ye raat naram aur roshan
                hoti hai, na zyada garmi na zyada sardi hoti hai, subah suraj
                dheemi roshni ke saath nikalta hai, aur farishte zameen par
                utarte hain. Lekin asli nishani yeh hai ke is raat ibadat karke
                dil mein sakoon aur Allah ki qurbat mehsoos hoti hai.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                27 Ramadan hi Shab-e-Qadr kyun mani jati hai?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                27 Ramadan ko Pakistan aur aksar Muslim countries mein
                Shab-e-Qadr isliye mana jata hai kyunke Sahih ahadith se yeh
                zyada qareen-e-qiyas hai. Lekin hakeeqat mein Shab-e-Qadr ka
                asli waqt Allah ne chhupa rakha hai taake log tamam aakhri 10
                raaton mein ibadat karen. Isliye 21, 23, 25, 27, aur 29 Ramadan
                tamam raaton ko ehtimam se guzarein.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Shab-e-Qadr mein kaun si namaz parhein?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Shab-e-Qadr mein Tahajjud namaz, nafl namaz (2-2 rakat se 12 ya
                100 rakat tak), aur Taraweeh namaz parhein. Har 2 rakat ke baad
                salam phir karein. Lambi qiraat ke saath namaz parhein aur sajde
                mein zyada dua karein. Maghrib aur Isha ki farz namaz jamat ke
                saath parhna na bhoolein.
              </p>
            </details>

            <details
              class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <summary
                class="font-semibold cursor-pointer text-gray-900 dark:text-gray-100"
              >
                Agar Shab-e-Qadr ki raat miss ho jaye to kya karein?
              </summary>
              <p class="mt-2 text-sm leading-relaxed">
                Agar ek taaq raat miss ho jaye to pareshaan na hon. Baqi taaq
                raaton mein ibadat ka ehtimam karein. Shab-e-Qadr ka asli waqt
                chhupa hai, isliye har taaq raat ko full dedication ke saath
                ibadat karein. Allah ki rehmat wasi hai aur har raat mein barkat
                hai. Tawba karke agli raat ki tayyari karein.
              </p>
            </details>
          </div>
        </div>

        <!-- Related Pages - Internal linking -->
        <div
          class="bg-blue-50 dark:bg-blue-950 rounded-lg p-4 border border-blue-200 dark:border-blue-800"
        >
          <p
            class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3"
          >
            Related Pages:
          </p>
          <div class="grid sm:grid-cols-2 gap-2">
            <a
              href="/itikaf/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Itikaf 2026 Dates
            </a>
            <a
              href="/calendar/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Ramadan Calendar 2026
            </a>
            <a
              href="/dua/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Ramzan Ki Duain
            </a>
            <a
              href="/shab-e-qadr/"
              class="text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Lailatul Qadr Guide
            </a>
          </div>
        </div>

        <!-- Final message -->
        <div
          class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl p-5 border border-emerald-200 dark:border-emerald-800"
        >
          <p
            class="text-sm leading-relaxed text-gray-700 dark:text-gray-300 text-center"
          >
            <strong class="text-emerald-700 dark:text-emerald-400">{{
              lang.content.message
            }}</strong>
            <br />
            <span class="text-xs mt-2 block text-gray-600 dark:text-gray-400">{{
              lang.content.pakistanMessage
            }}</span>
          </p>
        </div>
      </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { messages, locale } = useI18n();
const lang = computed(() => {
  const page = messages.value[locale.value]?.pages?.shabEQadr2026;
  return (
    page ?? {
      meta: {},
      header: {},
      dates: {},
      significance: { virtues: [] },
      worship: { activities: [], specialDua: {} },
      practical: { signs: [], tips: [] },
      content: {},
    }
  );
});

// Set meta tags
useHead({
  title: lang.value.meta?.title || "",
  meta: [
    { name: "description", content: lang.value.meta?.description || "" },
    { name: "keywords", content: lang.value.meta?.keywords || "" },
    // Open Graph
    { property: "og:title", content: lang.value.meta?.title || "" },
    { property: "og:description", content: lang.value.meta?.description || "" },
    { property: "og:type", content: "article" },
    // Twitter Card
    { name: "twitter:title", content: lang.value.meta?.title || "" },
    { name: "twitter:description", content: lang.value.meta?.description || "" },
  ],
});

// Add FAQ Schema
useSchemaOrg([
  {
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Shab-e-Qadr 2026 kab hai Pakistan mein?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Shab-e-Qadr 2026 Pakistan mein Ramadan ki aakhri 10 taaq raaton mein aati hai. Sabse zyada ehtemal 27 Ramadan ko hai jo 16 March 2026 ki raat hai. Lekin yeh 21, 23, 25, ya 29 Ramadan ki raat bhi ho sakti hai.",
        },
      },
      {
        "@type": "Question",
        name: "Shab-e-Qadr ki kya fazilat hai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Shab-e-Qadr hazar mahino se behtar hai. Is raat ki ibadat 83 saal 4 mahine ki ibadat se zyada ajar rakhti hai. Is raat farishte zameen par utarte hain aur subah tak salamti hoti hai.",
        },
      },
      {
        "@type": "Question",
        name: "Shab-e-Qadr mein kaunsi dua parhein?",
        acceptedAnswer: {
          "@type": "Answer",
          text: 'Shab-e-Qadr ki sabse aham dua hai: "Allahumma innaka afuwwun tuhibbul afwa fa\'fu anni" (Ya Allah tu maaf karne wala hai, tu mujhe maaf kar de). Is ke ilawa Quran tilawat, nafl namaz, tauba astaghfar aur darood sharif parhein.',
        },
      },
      {
        "@type": "Question",
        name: "Shab-e-Qadr ki pehchan kya hai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Shab-e-Qadr ki nishaniyan: Raat naram aur roshan hoti hai, na zyada garmi na sardi, subah suraj dheemi roshni ke saath nikalta hai, aur farishte zameen par utarte hain.",
        },
      },
      {
        "@type": "Question",
        name: "27 Ramadan hi Shab-e-Qadr kyun mani jati hai?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "27 Ramadan ko Pakistan mein isliye mana jata hai kyunke Sahih ahadith se yeh zyada qareen-e-qiyas hai. Lekin Allah ne asli waqt chhupa rakha hai taake log tamam aakhri 10 raaton mein ibadat karen.",
        },
      },
      {
        "@type": "Question",
        name: "Shab-e-Qadr mein kaun si namaz parhein?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Shab-e-Qadr mein Tahajjud namaz, nafl namaz (2-2 rakat se 12 ya 100 rakat tak), aur Taraweeh namaz parhein. Lambi qiraat ke saath namaz parhein aur sajde mein zyada dua karein.",
        },
      },
    ],
  },
]);
</script>

